import NoDataCard from '@/components/NoDataCard';
import { studyAlertConditionText } from '@/components/Studies/Create/Steps/Alerts/Alerts.utils';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { getCalculationsAndMeasurements } from '@/helpers';
import { _isEmpty } from '@/littledash';
import type { AlertDisplayData } from '@/model/Alert.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { Dispatch, FC, JSX } from 'react';
import { useMemo } from 'react';
import { RiRepeatFill } from 'react-icons/ri';
import './Alert.scss';

interface ConditionCellProps {
  calculations: Array<PresetCalculation>;
  alert: AlertDisplayData;
}

const ConditionCell: FC<ConditionCellProps> = ({ calculations, alert }) => {
  const calculation = useMemo(() => calculations?.find(({ id }) => id === alert?.calculation), [calculations, alert]);
  return (
    <span data-testid="alert-condition" className="alert-table-cell">
      {studyAlertConditionText({ alert, calculation, displayCalculationName: false, displayAction: false })}
    </span>
  );
};

const ActionCell: FC<{ alert: AlertDisplayData }> = ({ alert }) => {
  return (
    <span data-testid="alert-action" className="alert-table-cell">
      {alert.action}
    </span>
  );
};

const notificationToTypeString = (notification: AlertDisplayData['notification']): string => {
  switch (notification) {
    case 'warn':
      return 'Warning';
    case 'critical':
      return 'Critical';
    default:
      return '-';
  }
};

const AlertTypeCell: FC<{ alert: AlertDisplayData }> = ({ alert }) => {
  return (
    <div className="flex justify-between items-center w-100" data-testid="alert-type-container">
      <span data-testid="alert-type">{notificationToTypeString(alert?.notification)}</span>
      {(alert?.retrigger ?? false) && (
        <span
          data-testid="alert-retrigger-icon"
          data-tooltip-id="global-tooltip-id"
          data-tooltip-content="Alerts every time the condition is met"
        >
          <RiRepeatFill />
        </span>
      )}
    </div>
  );
};
type SelectedRows = Record<number, boolean>;

interface AlertsTableProps<T extends AlertDisplayData> {
  alerts: Array<T>;
  calculations: Array<PresetCalculation>;
  disabled?: boolean;
  onAlertClick: (alert: T) => void;
  onAddAlertClick: () => void;
  selectedRows?: SelectedRows;
  onSelectedRowsChange?: (selectedRows: SelectedRows) => void | Dispatch<SelectedRows>;
}

export const AlertsTable = <T extends AlertDisplayData>({
  alerts,
  calculations,
  disabled = false,
  onAlertClick,
  onAddAlertClick,
  selectedRows,
  onSelectedRowsChange,
}: AlertsTableProps<T>): JSX.Element => {
  const calculationsMap = getCalculationsAndMeasurements(calculations);

  const columns: Array<TanStackColumnDef<T>> = [
    {
      id: 'metric',
      header: 'Metric',
      size: 50,
      enableSorting: true,
      cell: ({ row: { original } }) => {
        if (disabled) {
          return <span data-testid="alert-metric">{calculationsMap?.get(original?.calculation)?.name ?? '-'}</span>;
        }
        return (
          <a className="link" onClick={() => onAlertClick?.(original)} data-testid="alert-metric">
            {calculationsMap?.get(original?.calculation)?.name ?? '-'}
          </a>
        );
      },
    },
    {
      id: 'condition',
      header: 'Condition',
      enableSorting: true,
      cell: ({ row: { original } }) => (
        <ConditionCell alert={original} calculations={Array.from(calculationsMap.values())} />
      ),
    },
    {
      id: 'notification',
      header: 'Alert Type',
      enableSorting: true,
      cell: ({ row: { original } }) => <AlertTypeCell alert={original} />,
    },
    {
      id: 'action',
      header: 'Alert Action',
      cell: ({ row: { original } }) => <ActionCell alert={original} />,
    },
  ];

  const cardClasses = 'w-100 pa3';

  if (_isEmpty(calculations)) {
    return (
      <NoDataCard
        title="This study has no preset configured"
        text="Alerts cannot be configured on studies that do not have a preset"
        dark
        className={cardClasses}
      />
    );
  }

  if (_isEmpty(alerts)) {
    return (
      <NoDataCard
        title="This study has no alerts yet"
        text="A preview of your alerts will appear here"
        onLinkClick={disabled ? undefined : () => onAddAlertClick?.()}
        btnTxt={disabled ? undefined : 'Add an alert'}
        openModal={true}
        dark
        className={cardClasses}
      />
    );
  }

  return (
    <Table
      className="w-100 bt mt3"
      columns={columns}
      data={alerts}
      selectedRows={disabled ? undefined : selectedRows}
      setSelectedRows={disabled ? undefined : onSelectedRowsChange}
    />
  );
};
